// /stacks/traffic/dev/frontend/src/pages/Sightings.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import FilterBar from "../features/sightings/FilterBar";
import ImagesPanel from "../features/sightings/ImagesPanel";
import SightingsTable from "../features/sightings/SightingsTable";
import QuerySummary from "../features/sightings/QuerySummary"; // <-- added
import { readFromURL, writeToURL, rangeForPeriod } from "../lib/urlState";
import { fetchSightings, fetchMediaByDetectionId } from "../lib/sightingsApi";
import { fmtFullWithDow } from "../lib/dates";

export default function SightingsPage() {
  // 1) initialize from URL; expand presets to concrete start/end
  const initial = useMemo(() => readFromURL(location.search), []);
  const [q, setQ] = useState(() => {
    let base = { ...initial };
    if (initial?.period && initial.period !== "custom") {
      const r = rangeForPeriod(initial.period);
      base = { ...base, ...r };
    }
    // default cameras if none present
    if (!base?.cameras?.length) base.cameras = ["41", "cheltenham", "pool"];
    if (!base.order) base.order = "desc";          // newest first default
    if (!base.limit) base.limit = 100;             // page size default
    if (!base.offset && base.offset !== 0) base.offset = 0;
    return base;
  });

  // 2) data + media state
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // ImagesPanel context + files
  const [mediaCtx, setMediaCtx] = useState(null);  // { plate, camera, time }
  const [media, setMedia] = useState(null);        // { vehicle, background, plate }
  const imagesRef = useRef(null);

  // --- DEBUG: render snapshot of q ---
  console.log("[Sightings.render] q =", q);

  // 3) fetch sightings
  const run = async (query) => {
    setLoading(true);
    try {
      const { rows, total } = await fetchSightings(query);
      setRows(rows);
      setTotal(total);
      // auto-load first row’s media if available
      if (rows?.[0]?.detection_id) {
        await onRowClick(rows[0], { silent: true });
      } else {
        setMediaCtx(null);
        setMedia(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { run(q); /* initial load */ }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 4) apply filter changes + keep URL in sync
  const apply = async (partial) => {
    const next = { ...q, ...(partial || {}) };

    // Normalize mutually exclusive chips (ensure NEVER both in query)
    if (next.is_night === true || next.is_night === false) next.buckets = [];
    if (next.buckets && next.buckets.length) next.is_night = null;

    // --- DEBUG: show the next state we are about to apply ---
    console.log("[Sightings.apply] partial =", partial, " -> next =", next);

    setQ(next);
    writeToURL(next, true);   // replace (keep history clean)
    await run(next);          // re-query immediately
  };

  // 5) paging (limit = page size only)
  const onPrev = () => apply({ offset: Math.max(0, (q.offset || 0) - (q.limit || 100)) });
  const onNext = () => apply({ offset: (q.offset || 0) + (q.limit || 100) });

  // 6) row click -> fetch images
  const onRowClick = async (r, { silent = false } = {}) => {
    const detectionId = r?.detection_id || r?.id;
    if (!detectionId) return;

    if (!silent) setMedia(null);

    const meta = {
      plate:  r?.plate ?? r?.updated_camera_prediction ?? "",
      camera: r?.camera ?? r?.camera_name ?? "",
      time:   fmtFullWithDow(r?.ts ?? r?.image_timestamp ?? ""),
    };
    setMediaCtx(meta);

    try {
      const m = await fetchMediaByDetectionId(detectionId);
      setMedia(m?.files || null);
    } catch {
      setMedia(null);
    }

    // Smooth scroll images into view when user clicks a row
    if (!silent && imagesRef.current) {
      imagesRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="page">
      <h1>Sightings</h1>

      {/* Stats-style filters */}
      <FilterBar value={q} onChange={apply} />

      {/* Live query summary (new) */}
      <QuerySummary
        start={q.start}
        end={q.end}
        cameras={q.cameras}
        plate={q.plate}
        exact={!!q.exact}
        total={total}
        visible={rows.length}
        buckets={q.buckets}
        is_night={q.is_night}
      />

      {/* Images ABOVE results (sticky) */}
      <div ref={imagesRef} style={{ position: "sticky", top: 8, zIndex: 5 }}>
        <ImagesPanel context={mediaCtx} files={media} />
      </div>

      {/* Results (2-col on wide, full row clickable) */}
      <SightingsTable
        rows={rows}
        total={total}
        limit={q.limit || 100}
        offset={q.offset || 0}
        order={q.order || "desc"}
        onPrev={onPrev}
        onNext={onNext}
        onRowClick={onRowClick}
      />

      {loading && <div className="mt-3 text-sm opacity-70">Loading…</div>}
    </div>
  );
}
